import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Nanoseconds, Timer } from '@spotify-internal/semantic-metrics';

import { MESSAGING_ENABLED } from 'utils/config';
import { sendTimerMetric, sendCounterMetric } from 'utils/metrics';
import { setup, startLiveEngage } from 'lib/client/liveEngage';
import { getLiveEngageLocale } from 'lib/client/liveEngage/utils';
import { DEFAULT_LOCALE } from 'utils/i18n/localeConfig';
import { isEnglishSupportOnlyLocale } from 'utils/market/market';

interface MessagingType {
  status: MessagingStatus;
  startMessaging?: () => void;
}

interface MessagingContextProps {
  locale: string;
  marketCode: string;
  children: React.ReactNode;
}

export enum MessagingAvailabilityReason {
  LOADING = 'loading',
  AVAILABLE = 'available',
  UNSUPPORTED_LOCALE = 'unsupported locale',
  TIMEOUT = 'timeout',
  DISABLED = 'disabled',
}

export interface MessagingStatus {
  isAvailable: boolean;
  reason: MessagingAvailabilityReason;
}

const DEFAULT_STATUS = {
  isAvailable: false,
  reason: MessagingAvailabilityReason.LOADING,
};

const DISABLED_STATUS = {
  isAvailable: false,
  reason: MessagingAvailabilityReason.DISABLED,
};

const sendLiveEngageMetrics = (
  setupTime: Nanoseconds<number>,
  locale: string,
  resolution: MessagingAvailabilityReason,
  pathname: string,
) => {
  sendTimerMetric('live-engage-setup-time', setupTime, {
    locale,
    resolution,
    pathname,
  });
  sendCounterMetric('live-engage-setup-count', {
    locale,
    resolution,
    pathname,
  });
};

export const MessagingContext = React.createContext<MessagingType>({
  status: DEFAULT_STATUS,
});

const MessagingProvider = ({ locale, marketCode, children }: MessagingContextProps) => {
  const [status, setStatus] = useState(MESSAGING_ENABLED ? DEFAULT_STATUS : DISABLED_STATUS);
  const localeToUse = isEnglishSupportOnlyLocale(locale) ? DEFAULT_LOCALE : locale;
  const liveEngageLocale = getLiveEngageLocale(localeToUse);

  const { pathname } = useRouter();

  const valueProp: MessagingType = {
    status,
    startMessaging: status.isAvailable
      ? () => {
          startLiveEngage(marketCode, liveEngageLocale);
        }
      : undefined,
  };

  useEffect(() => {
    if (!MESSAGING_ENABLED) return;

    sendCounterMetric('live-engage-setup-started', { locale: liveEngageLocale, pathname });

    const getLiveEngagestatus = async () => {
      const timer = Timer.start();
      const MessagingStatus = await setup(liveEngageLocale);
      sendLiveEngageMetrics(timer.end().nanoseconds, liveEngageLocale, MessagingStatus.reason, pathname);
      setStatus(MessagingStatus);
    };
    getLiveEngagestatus();
  }, [liveEngageLocale, pathname]);
  return <MessagingContext.Provider value={valueProp}>{children}</MessagingContext.Provider>;
};

export default MessagingProvider;
